import { useState, useContext, useEffect } from 'react'
import {
  cancelSubscription,
  reactivateSubscription,
  getSubscription,
  removePendingChange,
} from '../../services/SubscriptionApiClient'
import SubscriptionCard from '../Cards/SubscriptionCard'
import {
  BaseSubscriptionType,
  SubscriptionType,
  PlanCode,
} from '../../types/types'
import Spinner from '../../components/Spinner'
import { CouponContext } from '../context'
import { getUserId } from '../../services/TokenClient'
import { ExitSurveyModal } from './ExitSurveyModal'
import * as Sentry from '@sentry/nextjs'

type propTypes = {
  currentSubscription: SubscriptionType
  setCurrentSubscription: Function
}

const defaultCurrency = 'USD'

export default function SubscriptionPlanComponent(props: propTypes) {
  const { currentSubscription, setCurrentSubscription } = props
  const updateCoupons = useContext(CouponContext).updateCoupons

  const [loading, setLoading] = useState(true)
  const [showExitSurvey, setShowExitSurvey] = useState(false)
  const [surveyUrl, setSurveyUrl] = useState('')
  const [availableSubscriptions, setAvailableSubscriptions] = useState<
    BaseSubscriptionType[]
  >([])

  const initExitSurvey = () => {
    setSurveyUrl(`https://www.surveymonkey.com/r/D6T7XD7?U=${getUserId()}`)
  }

  useEffect(() => {
    async function getData() {
      if (loading) {
        let availableSubscriptions = [
          {
            planName: 'Reason+ Monthly',
            planCode: PlanCode.MONTHLY,
            price: '19.99',
            currency: defaultCurrency,
          },
          {
            planName: 'Reason+ Annual',
            planCode: PlanCode.ANNUAL,
            price: '199.00',
            currency: defaultCurrency,
          },
        ]
        try {
          if (
            currentSubscription.planCode === PlanCode.ARCADE_BUNDLE ||
            currentSubscription.pendingChange?.planCode ===
              PlanCode.ARCADE_BUNDLE
          ) {
            const isGbp = currentSubscription?.currency === 'GBP'
            availableSubscriptions = [
              ...availableSubscriptions,
              {
                planName: 'Reason+ Arcade',
                planCode: PlanCode.ARCADE_BUNDLE,
                price: isGbp ? '239.00' : '249.00',
                currency: defaultCurrency,
              },
            ]
          }
        } catch (e) {
          // I have no idea what the best way to display the error here is,
          // just not showing the plan feels like the most okay solution
          Sentry.captureException(e)
        } finally {
          setAvailableSubscriptions(availableSubscriptions)
          setLoading(false)
        }
      }
    }
    getData()
  }, [currentSubscription, loading])

  const cancelOrReactivateSubscription = async () => {
    setLoading(true)
    if (currentSubscription.state === 'canceled') {
      setCurrentSubscription(
        await reactivateSubscription(currentSubscription.id),
      )
    } else {
      setCurrentSubscription(await cancelSubscription(currentSubscription.id))
      initExitSurvey()
      setTimeout(() => setShowExitSurvey(true), 1000)
    }
    setLoading(false)
  }

  const reloadSubscription = async () => {
    setCurrentSubscription(await getSubscription(currentSubscription.id))
  }

  const cancelChange = async () => {
    setLoading(true)
    if (currentSubscription.pendingChange) {
      await removePendingChange(currentSubscription.id)
    }
    setCurrentSubscription(await getSubscription(currentSubscription.id))
    setLoading(false)
  }

  // If the user has an active subscription, pick the currency from that
  const getCurrency = () => {
    if (currentSubscription) {
      return currentSubscription.currency
    }
    return defaultCurrency
  }

  // "nt"-agnostic comparison of subscription types
  const isEquivalentSubscriptionType = (
    sub1: BaseSubscriptionType,
    sub2: BaseSubscriptionType,
  ) => {
    return sub1.planCode.replace(/nt$/, '') === sub2.planCode.replace(/nt$/, '')
  }

  return (
    <>
      <div className="plus-card d-flex flex-column position-relative overflow-hidden bg-white-noise py-5 py-lg-20 px-5 px-lg-15">
        <h5 className="text-center color-stone-2 mb-10 mb-lg-20">
          <strong>Choose your plan:</strong>
        </h5>
        {loading ? (
          <Spinner />
        ) : (
          <div className="d-flex flex-column flex-md-row justify-content-between">
            {availableSubscriptions.map((availableSub) => {
              // Render SubscriptionCards based on the basic (hardcoded) available
              // subscriptions. Pass in the currentSubscription (which contains user
              // specific data from recurly) if it matches the current rendered card
              const s = isEquivalentSubscriptionType(
                currentSubscription,
                availableSub,
              )
                ? currentSubscription
                : availableSub
              return (
                <SubscriptionCard
                  subscription={s}
                  key={s.planCode}
                  cancelOrReactivateSubscription={
                    cancelOrReactivateSubscription
                  }
                  cancelChange={cancelChange}
                  reloadSubscription={reloadSubscription}
                  currency={getCurrency()}
                  currentSubscription={currentSubscription}
                  hasPendingChange={
                    currentSubscription.pendingChange?.planCode === s.planCode
                  }
                />
              )
            })}
          </div>
        )}
      </div>
      <ExitSurveyModal
        show={showExitSurvey}
        surveyUrl={surveyUrl}
        onReject={() => setShowExitSurvey(false)}
      />
    </>
  )
}
